/* eslint-disable default-case */
import produce from 'immer';
import get from 'lodash.get';
import * as Sentry from '@sentry/browser';
import { access, create } from 'services/api';
import { immerMergeObject, immerSet } from './util';

export const ACCESS_SATELLITE_TOKEN_RECEIVED = 'create/access/SATELLITE_TOKEN_RECEIVED';
export const ACCESS_EPISODE_FETCH_REQUESTED = 'create/access/EPISODE_FETCH_REQUESTED';
export const ACCESS_EPISODE_FETCH_RECEIVED = 'create/access/EPISODE_FETCH_RECEIVED';
export const ACCESS_EPISODE_FETCH_FAILED = 'create/access/EPISODE_FETCH_FAILED';
export const ACCESS_EPISODE_SAVE_REQUESTED = 'create/access/EPISODE_SAVE_REQUESTED';
export const ACCESS_EPISODE_SAVE_RECEIVED = 'create/access/EPISODE_SAVE_RECEIVED';
export const ACCESS_EPISODE_SAVE_FAILED = 'create/access/EPISODE_SAVE_FAILED';
export const ACCESS_EPISODE_UPDATE_FIELD = 'create/access/EPISODE_UPDATE_FIELD';
export const ACCESS_SHOW_FETCH_REQUESTED = 'create/access/SHOW_FETCH_REQUESTED';
export const ACCESS_SHOW_FETCH_RECEIVED = 'create/access/SHOW_FETCH_RECEIVED';
export const ACCESS_SHOW_FETCH_FAILED = 'create/access/SHOW_FETCH_FAILED';
export const ACCESS_SHOW_UPDATE_REQUESTED = 'create/access/SHOW_UPDATE_REQUESTED';
export const ACCESS_SHOW_UPDATE_RECEIVED = 'create/access/SHOW_UPDATE_RECEIVED';
export const ACCESS_SHOW_UPDATE_FAILED = 'create/access/SHOW_UPDATE_FAILED';

const serializeEpisode = data => ({
  locked: data.locked || false,
  privateIntro: data.privateIntro || null,
  privateOutro: data.privateOutro || null,
  publicIntro: data.publicIntro || null,
  publicOutro: data.publicOutro || null,
});

export const selectSatelliteAuth = state => {
  return {
    showId: get(state, ['v2', 'access', 'showId'], null),
    token: get(state, ['v2', 'access', 'token'], null),
  };
};

export const selectShowAccessLoadingStatus = state => {
  return get(state, ['v2', 'access', 'show', 'loading'], false);
};

export const selectShowAccessSettings = state => {
  return get(state, ['v2', 'access', 'show', 'data']);
};

export const selectEpisodeAccessSettings = (state, episodeId) => {
  return get(state, ['v2', 'access', 'episodes', episodeId, 'data']);
};

export const selectEpisodeAccessLoadingStatus = (state, episodeId) => {
  return get(state, ['v2', 'access', 'episodes', episodeId, 'loading'], false);
};

export const selectEpisodeAccessHasChanged = (state, episodeId) => {
  return get(state, ['v2', 'access', 'episodes', episodeId, 'changed'], false);
};

const initialState = {
  episodes: {},
  show: {
    error: false,
    loading: false,
    data: {},
  },
  showId: null,
  token: null,
};

export default produce((state, { type, payload }) => {
  switch (type) {
    case ACCESS_SATELLITE_TOKEN_RECEIVED: {
      immerSet(state, ['token'], payload.token);
      immerSet(state, ['showId'], payload.showId);
      break;
    }

    case ACCESS_EPISODE_FETCH_REQUESTED: {
      immerSet(state, ['episodes', payload.episodeId], {
        changed: false,
        error: false,
        loading: true,
        data: {},
      });
      break;
    }

    case ACCESS_EPISODE_FETCH_RECEIVED: {
      immerSet(state, ['episodes', payload.episodeId], {
        changed: false,
        error: false,
        loading: false,
        data: payload.data,
      });
      break;
    }

    case ACCESS_EPISODE_FETCH_FAILED: {
      immerMergeObject(state, ['episodes', payload.episodeId], {
        error: true,
        loading: false,
      });
      break;
    }

    case ACCESS_EPISODE_UPDATE_FIELD: {
      immerSet(state, ['episodes', payload.episodeId, 'changed'], true);
      immerMergeObject(state, ['episodes', payload.episodeId, 'data'], payload.data);
      break;
    }

    case ACCESS_EPISODE_SAVE_REQUESTED: {
      immerMergeObject(state, ['episodes', payload.episodeId, 'saving'], true);
      break;
    }

    case ACCESS_EPISODE_SAVE_RECEIVED: {
      immerMergeObject(state, ['episodes', payload.episodeId], {
        changed: false,
        data: payload.data,
        error: false,
        saving: false,
      });
      break;
    }

    case ACCESS_EPISODE_SAVE_FAILED: {
      immerSet(state, ['episodes', payload.episodeId], {
        error: true,
        loading: false,
        data: payload.data,
        saving: false,
      });
      break;
    }

    case ACCESS_SHOW_FETCH_REQUESTED:
    case ACCESS_SHOW_UPDATE_REQUESTED: {
      immerSet(state, ['show', 'loading'], true);
      break;
    }

    case ACCESS_SHOW_FETCH_RECEIVED:
    case ACCESS_SHOW_UPDATE_RECEIVED: {
      immerSet(state, ['show', 'loading'], false);
      immerSet(state, ['show', 'data'], {
        title: payload.privateTitle || '',
        description: payload.privateDescription || '',
        image: payload.privateImage || null,
      });
      break;
    }

    case ACCESS_SHOW_UPDATE_FAILED:
    case ACCESS_SHOW_FETCH_FAILED: {
      immerMergeObject(state, ['show'], {
        error: true,
        loading: false,
      });
      break;
    }
  }
}, initialState);

export const createSatelliteToken = (showId, dispatch, state) => {
  const { showId: storedShowId, token: storedToken } = selectSatelliteAuth(state);

  if (storedToken && storedShowId === showId) {
    return Promise.resolve(storedToken);
  }

  return create
    .get(`/api-v3/satelliteToken/access/${showId}`)
    .then(({ body: { token } }) => {
      dispatch({
        type: ACCESS_SATELLITE_TOKEN_RECEIVED,
        payload: { showId, token },
      });

      return token;
    })
    .catch(error => {
      Sentry.captureException(error);
    });
};

export const fetchShowAccessInfo = showId => (dispatch, getState) => {
  const state = getState();

  dispatch({ type: ACCESS_SHOW_FETCH_REQUESTED });

  return createSatelliteToken(showId, dispatch, state).then(token => {
    return access
      .get(`/api/v1/shows/${showId}`, { headers: { 'x-api-key': token } })
      .then(response => {
        dispatch({
          type: ACCESS_SHOW_FETCH_RECEIVED,
          payload: response.body,
        });
      })
      .catch(error => {
        dispatch({
          type: ACCESS_SHOW_FETCH_FAILED,
          payload: { showId },
        });

        Sentry.captureException(error);
      });
  });
};

export const updateShowAccessInfo = (showId, { title, description, image } = {}) => async (
  dispatch,
  getState
) => {
  const payload = { privateTitle: title, privateDescription: description, privateImage: image };

  dispatch({ type: ACCESS_SHOW_UPDATE_REQUESTED });

  return createSatelliteToken(showId, dispatch, getState()).then(token => {
    return access
      .put(`/api/v1/shows/${showId}`, payload, { headers: { 'x-api-key': token } })
      .then(response => {
        dispatch({
          type: ACCESS_SHOW_UPDATE_RECEIVED,
          payload: response.body,
        });
      })
      .catch(() => {
        return access
          .post(`/api/v1/shows`, { showId, ...payload }, { headers: { 'x-api-key': token } })
          .then(response => {
            dispatch({
              type: ACCESS_SHOW_UPDATE_RECEIVED,
              payload: response.body,
            });
          })
          .catch(error => {
            dispatch({
              type: ACCESS_SHOW_UPDATE_FAILED,
              payload: { showId },
            });

            Sentry.captureException(error);
          });
      });
  });
};

export const fetchEpisodeAccessSettings = (showId, episodeId) => (dispatch, getState) => {
  const state = getState();
  const settings = selectEpisodeAccessSettings(state, episodeId);

  if (settings) {
    return Promise.resolve();
  }

  dispatch({
    type: ACCESS_EPISODE_FETCH_REQUESTED,
    payload: { episodeId },
  });

  return createSatelliteToken(showId, dispatch, state).then(token => {
    return access
      .get(`/api/v1/shows/${showId}/episodes/${episodeId}`, { headers: { 'x-api-key': token } })
      .then(response => {
        dispatch({
          type: ACCESS_EPISODE_FETCH_RECEIVED,
          payload: {
            episodeId,
            data: serializeEpisode(response.body),
          },
        });
      })
      .catch(error => {
        dispatch({
          type: ACCESS_EPISODE_FETCH_FAILED,
          payload: { episodeId },
        });

        Sentry.captureException(error);
      });
  });
};

export const updateEpisodeAccessSettings = ({ episodeId, data }) => dispatch => {
  dispatch({ type: ACCESS_EPISODE_UPDATE_FIELD, payload: { episodeId, data } });
};

export const saveEpisodeAccessSettings = ({ showId, episodeId }) => (dispatch, getState) => {
  const state = getState();
  const data = selectEpisodeAccessSettings(state, episodeId);

  dispatch({ type: ACCESS_EPISODE_SAVE_REQUESTED, payload: { episodeId } });

  const dispatchSuccess = response =>
    dispatch({
      type: ACCESS_EPISODE_SAVE_RECEIVED,
      payload: {
        data: serializeEpisode(response.body),
        episodeId,
      },
    });

  const updateExistingAccessEpisode = token =>
    access
      .put(`/api/v1/shows/${showId}/episodes/${episodeId}`, data, {
        headers: { 'x-api-key': token },
      })
      .then(dispatchSuccess);

  const createNewAccessEpisode = token =>
    access
      .post(
        `/api/v1/shows/${showId}/episodes`,
        { showId, id: episodeId, ...data },
        { headers: { 'x-api-key': token } }
      )
      .then(dispatchSuccess);

  return createSatelliteToken(showId, dispatch, state)
    .then(token => {
      return updateExistingAccessEpisode(token).catch(error => {
        if (error.status === 404) {
          return createNewAccessEpisode(token);
        }

        return Promise.reject(error);
      });
    })
    .catch(error => {
      dispatch({
        type: ACCESS_EPISODE_SAVE_FAILED,
        payload: { episodeId },
      });

      Sentry.captureException(error);
    });
};
